import axios from '@fs/zion-axios'
import { buildLangUrl } from '@fs/zion-router'
import { getMostRecentFieldValue } from '@fs/zion-sls'
import { getImageMetaData } from '@fs/zion-image-viewer'
import { getImagePermissions } from '../../../shared/services/platform/platformService'
import { getArtifact } from '../../../shared/services/rms/rmsService'
import { errorCollector } from '../../../shared/utils/utils'
import { normalizeForMatching } from '../../../shared/utils/stringNormalization'

// AI Prompt Configuration
const GENEALOGIST_SYSTEM_MESSAGE = {
  role: 'system',
  content: `You are a professional genealogist who reads historical vital records, such as birth, marriage, death, and census records.

The text you receive comes from automated handwriting or OCR recognition. This text often has errors. Common errors include: a wrong letter in a name, a split or joined word, a missing word, and missing or wrong punctuation. Use the surrounding context to work out the most likely correct reading before you extract facts.

Do not add a name, date, relationship, or place that the text does not support. If a detail is too unclear to read with confidence, write it as [unclear] instead of guessing.

These records are historical documents used for family history research. Some records use old-fashioned terms or record sensitive facts, such as illegitimacy, race, or cause of death. Treat this language as plain historical fact, not as offensive content, and report it factually in your summary.`,
}

const SUMMARIZATION_USER_PROMPT = (plainTextTranscript) => ({
  role: 'user',
  content: `
Extract genealogically significant information from this historical document.

INSTRUCTIONS:
- Consolidate family units: write "John and Mary Smith (married)" instead of separate entries for each spouse
- For parent-child relationships, combine into one entry: "John Smith, son of Thomas and Elizabeth Smith, married to Mary Jones"
- Only include names with genealogical context (vital events, relationships, locations)
- Omit standalone names without genealogical information
- Only include dates tied to vital events: birth, death, marriage, divorce, immigration
- Omit dates without genealogical significance
- Consolidate reciprocal relationships into single entries
- Be concise and avoid repeating information
- Do not use asterisks, bold text, or any markdown formatting
- If a name or date is unclear because of a transcription error, write [unclear] instead of guessing

OUTPUT FORMAT (follow this structure exactly):

Names and Relationships:
- [NAME1]: [relationship and associated names]
- [NAME2]: [relationship and associated names]

Dates:
- [DATE1]: [NAME1] - [event type and location]
- [DATE2]: [NAME2] - [event type and location]

Summary: [3-4 sentence summary of the document's genealogical content]

Rewrite the following text to be inoffensive and work with OpenAI's policy.

Historical document text:
${plainTextTranscript}`,
})

export async function getTextTranscript(url, plainTextTranscript) {
  try {
    const response = await axios.post(
      buildLangUrl(url),
      {
        messages: [GENEALOGIST_SYSTEM_MESSAGE, SUMMARIZATION_USER_PROMPT(plainTextTranscript)],
      },
      { headers: { Accept: 'text/plain' } }
    )
    return response.data
  } catch (error) {
    console.error('Error:', error)
  }
  return null
}

/**
 * Streams text transcript processing with real-time chunk delivery.
 *
 * This function intentionally uses await in a loop for streaming data processing.
 * Each chunk must be fully processed before proceeding to maintain proper streaming
 * behavior and ensure data integrity. The sequential processing is required for
 * the streaming response to work correctly with the onChunk callback.
 *
 * @param {string} url - The API endpoint URL for text processing
 * @param {string} plainTextTranscript - The raw text to be processed
 * @param {function} onChunk - Callback function called for each streamed chunk
 * @returns {Promise<string>} The complete processed response
 */
export async function getTextTranscriptStreaming(url, plainTextTranscript, onChunk) {
  try {
    const response = await fetch(buildLangUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [GENEALOGIST_SYSTEM_MESSAGE, SUMMARIZATION_USER_PROMPT(plainTextTranscript)],
      }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
    }

    if (!response.body) {
      throw new Error('Response body is null or undefined')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let fullResponse = ''

    try {
      let done = false
      while (!done) {
        // The use of 'await' in this loop is intentional and necessary for streaming.
        // We must wait for each chunk from the stream before proceeding to the next
        // iteration.
        // eslint-disable-next-line no-await-in-loop
        const result = await reader.read()
        done = result.done
        const value = result.value

        if (value) {
          const chunkText = decoder.decode(value, { stream: true })
          fullResponse += chunkText
          if (onChunk) {
            onChunk(chunkText, fullResponse)
          }
        }
      }

      return fullResponse
    } finally {
      reader.releaseLock()
    }
  } catch (error) {
    errorCollector(error, 'Error in streaming:')
    throw error
  }
}

/**
 * Matches a phrase by comparing normalized word sequences.
 * Extracts words from each field after normalization, skipping fields that
 * normalize to empty strings (e.g., special character fields like "↔").
 *
 * @param {string} keyword - The search phrase (e.g., "francisco y dolores maria")
 * @param {Array} fs - Array of field objects to check
 * @returns {boolean} - True if the normalized word sequence matches
 */
export const matchPhrase = (keyword, fs) => {
  // Extract normalized words from the keyword
  const keywordWords = normalizeForMatching(keyword)
    .split(/\s+/)
    .filter((w) => w.length > 0)

  // Extract normalized words from fields, skipping empty normalizations
  const fieldWords = []
  fs.forEach((f) => {
    const fieldValue = getMostRecentFieldValue(f)
    const normalized = normalizeForMatching(fieldValue)
    // Split and collect non-empty words
    const words = normalized.split(/\s+/).filter((w) => w.length > 0)
    fieldWords.push(...words)
  })

  // Compare word sequences
  if (keywordWords.length !== fieldWords.length) return false
  return keywordWords.every((word, i) => word === fieldWords[i])
}

/**
 * Builds a list of fields that match search keywords, handling both single
 * keywords and multi-word phrases. Normalizes text to handle diacritics and
 * special characters, skipping fields that normalize to empty strings.
 *
 * @param {Array<string>} keywords - Array of search terms (can be single words or phrases)
 * @param {Array} fields - Array of field objects from the transcript
 * @returns {Array} - Array of matching fields marked with isKeyword: true
 */
export function buildKeywordFields(keywords, fields) {
  const keywordFieldS = []
  const seenFields = new Set() // Use Set for O(1) duplicate checking
  if (!fields) return keywordFieldS

  // PERFORMANCE OPTIMIZATION: Precompute normalized values once per field
  // Previously normalized each field O(n) times in phrase matching loops
  // This reduces buildKeywordFields from O(n²) to O(n) complexity
  const fieldNormalizations = new Map() // field → normalized string
  const fieldWords = new Map() // field → normalized word array

  fields.forEach((field) => {
    const fieldValue = getMostRecentFieldValue(field)
    const normalized = normalizeForMatching(fieldValue)

    // Store full normalized string for single keyword matching
    fieldNormalizations.set(field, normalized)

    // Store word array for phrase matching
    const words = normalized.split(/\s+/).filter((w) => w.length > 0)
    fieldWords.set(field, words)
  })
  if (keywords)
    keywords.forEach((keyword) => {
      const words = keyword?.split(' ')
      if (words?.length > 1) {
        // Multi-word phrase matching
        // Extract normalized words from keyword
        const keywordWords = normalizeForMatching(keyword)
          .split(/\s+/)
          .filter((w) => w.length > 0)

        // Try starting at each field position
        for (let startIndex = 0; startIndex < fields.length; startIndex++) {
          const collectedWords = []
          const collectedFields = []
          let currentIndex = startIndex

          // Collect fields until we have enough normalized words
          while (collectedWords.length < keywordWords.length && currentIndex < fields.length) {
            const field = fields[currentIndex]

            // OPTIMIZATION: Use precomputed words (O(1)) instead of re-normalizing (O(m))
            const normalizedWords = fieldWords.get(field)

            // Always collect the field, even if it normalizes to empty (e.g., "↔")
            collectedFields.push(field)
            // But only collect its words if it has any
            collectedWords.push(...normalizedWords)
            currentIndex++
          }

          // Check if collected words match keyword words
          if (
            collectedWords.length === keywordWords.length &&
            collectedWords.every((word, i) => word === keywordWords[i])
          ) {
            // Mark all collected fields (including special char fields)
            collectedFields.forEach((field) => {
              if (!seenFields.has(field)) {
                seenFields.add(field)
                keywordFieldS.push(field)
              }
            })
          }
        }
      } else {
        // Single keyword matching using precomputed normalization
        const normalizedKeyword = normalizeForMatching(keyword)

        fields.forEach((f) => {
          // OPTIMIZATION: Use precomputed value (O(1)) instead of re-normalizing
          const normalizedValue = fieldNormalizations.get(f)

          if (normalizedKeyword === normalizedValue) {
            if (!seenFields.has(f)) {
              seenFields.add(f)
              keywordFieldS.push(f)
            }
          }
        })
      }
    })
  keywordFieldS.forEach((kf) => {
    kf.isKeyword = true
  })
  return keywordFieldS
}

/**
 * Determines the API endpoint based on feature flags.
 * Priority: OpenAI GPT-5 > Azure 4.1 > Azure 4.0 (fallback)
 *
 * @param {boolean} openAiGPT5FlagIsOn - Whether OpenAI GPT-5 endpoint is enabled
 * @param {boolean} azure41StreamingPromptFlagIsOn - Whether Azure 4.1 endpoint is enabled
 * @returns {string} The endpoint URL to use
 */
function getAiEndpoint(openAiGPT5FlagIsOn, azure41StreamingPromptFlagIsOn) {
  if (openAiGPT5FlagIsOn) {
    return '/records/improve/openAiGPT5'
  }
  if (azure41StreamingPromptFlagIsOn) {
    return '/records/improve/azureAiPrompt41'
  }
  return '/records/improve/azureAiPrompt'
}

/**
 * Determines if streaming mode should be used.
 * Requires both flag support and callback functions.
 *
 * @param {boolean} openAiGPT5FlagIsOn - Whether OpenAI GPT-5 endpoint is enabled
 * @param {boolean} azure41StreamingPromptFlagIsOn - Whether Azure 4.1 endpoint is enabled
 * @param {Function} setStreamingContent - Callback to set streaming content
 * @param {Function} setIsStreaming - Callback to set streaming state
 * @returns {boolean} Whether streaming should be used
 */
function shouldUseStreaming(openAiGPT5FlagIsOn, azure41StreamingPromptFlagIsOn, setStreamingContent, setIsStreaming) {
  const flagSupportsStreaming = openAiGPT5FlagIsOn || azure41StreamingPromptFlagIsOn
  const hasStreamingCallbacks = setStreamingContent && setIsStreaming
  return flagSupportsStreaming && hasStreamingCallbacks
}

/**
 * Executes a streaming AI request with proper state management.
 *
 * @param {string} endpoint - The API endpoint URL
 * @param {string} plainTextTranscript - The text to process
 * @param {Function} setStreamingContent - Callback to set streaming content
 * @param {Function} setIsStreaming - Callback to set streaming state
 * @param {Function} setLoading - Callback to set loading state
 * @param {Function} isStale - Returns true if the request's image is no longer the one on screen
 * @returns {Promise<string>} The complete response text
 */
async function executeStreamingRequest(
  endpoint,
  plainTextTranscript,
  setStreamingContent,
  setIsStreaming,
  setLoading,
  isStale
) {
  setIsStreaming(true)
  setLoading(false) // Turn off loading immediately so skeleton can show

  const response = await getTextTranscriptStreaming(endpoint, plainTextTranscript, (chunk, fullResponse) => {
    // Drop chunks for a request whose image the user has since navigated away from,
    // so a stale stream can't paint over the summary for the image now on screen.
    if (!isStale()) setStreamingContent(fullResponse)
  })

  if (!isStale()) {
    setIsStreaming(false)
    setStreamingContent('') // Clear streaming content once complete
  }

  return response
}

export const fetchSummarization = async ({
  currentImageArk,
  currentImageArkRef,
  plainTextTranscript,
  cachedSummarizations,
  setSummarizationData,
  setCachedSummarizations,
  setLoading,
  setSummaryError,
  azure41StreamingPromptFlagIsOn,
  openAiGPT5FlagIsOn,
  parseTranscript,
  hasMeaningfulContent,
  setStreamingContent,
  setIsStreaming,
}) => {
  // True once the user has navigated to a different image while this request was in flight.
  const isStale = () => Boolean(currentImageArkRef) && currentImageArkRef.current !== currentImageArk
  try {
    // Early returns for missing data
    if (!plainTextTranscript || !currentImageArk) {
      setSummarizationData(null)
      setLoading(false)
      return
    }

    // Check cache
    if (cachedSummarizations[currentImageArk]) {
      setSummarizationData(cachedSummarizations[currentImageArk])
      setLoading(false)
      return
    }

    setLoading(true)

    // Validate content
    if (!hasMeaningfulContent(plainTextTranscript)) {
      setSummarizationData(null)
      setLoading(false)
      return
    }

    // Determine endpoint and mode using conditional variables
    const endpoint = getAiEndpoint(openAiGPT5FlagIsOn, azure41StreamingPromptFlagIsOn)
    const useStreaming = shouldUseStreaming(
      openAiGPT5FlagIsOn,
      azure41StreamingPromptFlagIsOn,
      setStreamingContent,
      setIsStreaming
    )

    // Execute request based on mode (single conditional instead of 5 nested ones)
    let response
    if (useStreaming) {
      response = await executeStreamingRequest(
        endpoint,
        plainTextTranscript,
        setStreamingContent,
        setIsStreaming,
        setLoading,
        isStale
      )
    } else {
      response = await getTextTranscript(endpoint, plainTextTranscript)
    }

    // Parse and cache response. The cache is keyed by the request's own image ark, so it's
    // still safe to store even if the user has since navigated to a different image.
    const parsedData = parseTranscript(response?.result || response)
    setCachedSummarizations((prevTranscripts) => ({
      ...prevTranscripts,
      [currentImageArk]: parsedData,
    }))
    if (isStale()) return
    setSummarizationData(parsedData)
    setSummaryError(false)
  } catch (error) {
    if (isStale()) return
    if (setIsStreaming) setIsStreaming(false)
    if (setStreamingContent) setStreamingContent('')
    setSummaryError(true)
    errorCollector(error, currentImageArk, {
      codeId: 29,
    })
  } finally {
    // Only set loading to false if not in streaming mode (streaming mode already set it to false)
    const wasStreaming = shouldUseStreaming(
      openAiGPT5FlagIsOn,
      azure41StreamingPromptFlagIsOn,
      setStreamingContent,
      setIsStreaming
    )
    if (!wasStreaming && !isStale()) {
      setLoading(false)
    }
  }
}

export const getImagePermissionsBoolean = async (partitionKey, dispatch) => {
  if (partitionKey !== undefined) {
    const data = await getImagePermissions(null, partitionKey)
    dispatch({
      type: 'SET_IMAGE_PERMISSION',
      value: data,
    })
  }
}

export const getDownloadPermission = async (imageArk, dispatch) => {
  if (!imageArk) return

  try {
    const metadata = await getImageMetaData(imageArk)
    // Use the same download property that ComboViewer uses
    const downloadAllowed = metadata?.download ?? false

    dispatch({
      type: 'SET_DOWNLOAD_PERMISSION',
      value: downloadAllowed,
    })
  } catch (error) {
    console.error('[FullText] Failed to fetch download permission:', error)
    // Default to restricted on error (fail-safe)
    dispatch({
      type: 'SET_DOWNLOAD_PERMISSION',
      value: false,
    })
  }
}

export const fetchArtifact = async (thumbnails, currentImageArk, setArtifact, setArtifactLoadErr) => {
  const apid = thumbnails?.find((thumb) => thumb.ark === currentImageArk)?.id
  if (apid) {
    try {
      const artifactData = await getArtifact(apid)
      setArtifact(artifactData)
    } catch (err) {
      errorCollector(err, currentImageArk, {
        codeId: 25,
      })
      setArtifactLoadErr(true)
    }
  }
}
